<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-1">
      <button
        data-test="open-shared-funds"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <span>
        {{ $t('HeadContract.ProgressClaimsShowLite.table.sharedFunds') }}
      </span>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget) }}
    </td>

    <td class="bg-green-100/50" />

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/50"
    >
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-100/50" />

    <td class="bg-green-100/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/50"
      :class="{ 'text-red-600': totalCosts.variance !== 0 }"
    >
      {{ australianCurrency(totalCosts.variance) }}
    </td>

    <td class="bg-green-100/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-100/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-blue-100/50"
    >
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="!claim.capture_claim_amount || showPercentages"
      class="bg-blue-100/50"
    >
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget - totalCosts.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <SharedFundCostRow
      v-for="fund in sharedFunds"
      :key="fund.id"
      :shared-fund="fund"
      :claim="claim"
      :show-percentages="showPercentages"
    />

    <tr
      v-if="sharedFunds.length === 0"
      class="bg-gray-50"
    >
      <td
        colspan="100%"
        class="text-center p-4 text-gray-500"
      >
        {{ $t('HeadContract.ProgressClaimsShowLite.table.noSharedFunds') }}
      </td>
    </tr>
  </template>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber } from '@/helpers';
import Claim from '@/models/Claim';
import SharedFund from '@/models/SharedFund';
import { useSharedFundCostsStore } from '@/store/sharedFundCosts';
import { computed, ref } from 'vue';
import SharedFundCostRow from './SharedFundCostRow.vue';

const props = defineProps<{
  sharedFunds: SharedFund[];
  claim: Claim;
  showPercentages?: boolean;
}>();

const sharedFundCostsStore = useSharedFundCostsStore();

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
  };

  props.sharedFunds.forEach((fund) => {
    totals.budget += fund.budget;
    totals.previous += fund.previous(props.claim);

    const sharedFundCost = sharedFundCostsStore.models
      .where('shared_fund_id', fund.id)
      .where('claim_id', props.claim.id)
      .first();

    if(sharedFundCost) {
      totals.currentClaim += sharedFundCost.current_claim || 0;
      totals.currentCertified += sharedFundCost.current_certified || 0;
      totals.variance += (sharedFundCost.current_claim || 0) - (sharedFundCost.current_certified || 0);
    }
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const open = ref(false);

defineOptions({
  inheritAttrs: false,
});
</script>

<style scoped></style>
